import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import AnimatedImage from "@/components/AnimatedImage";
import { useReveal } from "@/hooks/useReveal";
import logoAquadias from "@/assets/logo-aquadias.png";
import dispenserVarIde from "@/assets/dispenser-var-ide.webp";
import handDispenser from "@/assets/hand-dispenser.webp";
import allaVitaminer from "@/assets/alla-vitaminer-liggandes.webp";
import logoAdidas from "@/assets/logo-adidas.png";
import logoBravida from "@/assets/logo-bravida.png";
import logoBahnhof from "@/assets/logo-bahnhof.png";
import logoCorem from "@/assets/logo-corem.png";
import logoHfab from "@/assets/logo-hfab.png";
import logoIkag from "@/assets/logo-ikag.png";
import logoLiwlig from "@/assets/logo-liwlig.png";
import logoNorvestor from "@/assets/logo-norvestor.png";
import logoOneflow from "@/assets/logo-oneflow.png";
import logoSabis from "@/assets/logo-sabis.png";

const clientLogos = [
  { src: logoBravida, alt: "Bravida" },
  { src: logoAdidas, alt: "Adidas" },
  { src: logoOneflow, alt: "Oneflow" },
  { src: logoBahnhof, alt: "Bahnhof" },
  { src: logoCorem, alt: "Corem" },
  { src: logoHfab, alt: "HFAB" },
  { src: logoIkag, alt: "IKAG" },
  { src: logoLiwlig, alt: "Liwlig" },
  { src: logoNorvestor, alt: "Norvestor" },
  { src: logoSabis, alt: "Sabis" },
];

const RevealDiv = ({
  children,
  delay = 0,
  fromRight = false,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  fromRight?: boolean;
  className?: string;
}) => {
  const { ref, className: revealClass } = useReveal({ fromRight });
  return (
    <div ref={ref} className={`${revealClass} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const FadeUp = ({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      className={`fade-up ${visible ? "visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Index = () => {
  const heroContent = (
    <section className="relative h-[55vh] flex items-center justify-center overflow-hidden">
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        // @ts-expect-error fetchpriority is a valid HTML attribute
        fetchpriority="high"
        width={1920}
        height={1080}
        className="absolute inset-0 w-full h-full object-cover"
        poster="/hero-poster.jpg"
      >
        <source src="/hero-video.webm" type="video/webm" />
      </video>
      <div className="absolute inset-0 bg-coral-500/40" />
      <div className="relative z-10 flex items-center justify-center">
        <img
          src={logoAquadias}
          alt="aQuadias"
          className="h-24 md:h-36 w-auto animate-thrive-in"
        />
      </div>
    </section>
  );

  return (
    <Layout heroSlot={heroContent}>
      <Seo
        title="Aquadias – En friskare arbetsplats"
        description="Vitamindispensrar och brustabletter med naturliga smaker. Ge kontoret en friskare vardag med Aquadias prenumerationstjänst."
        path="/"
      />
      {/* Client logos — use the space above the carousel as a real transition zone into cream */}
      <section className="relative z-10 -mt-40 md:-mt-48 pt-40 md:pt-48 overflow-x-hidden overflow-y-visible">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-40 md:h-48 bg-gradient-to-b from-transparent via-cream-100/50 to-cream-100"
        />
        <div className="relative overflow-x-hidden bg-cream-100 pb-3 md:pb-4">
          <div className="flex animate-marquee w-max gap-12 md:gap-16">
            {[...clientLogos, ...clientLogos].map((logo, i) => (
              <img
                key={`${logo.alt}-${i}`}
                src={logo.src}
                alt={logo.alt}
                className="h-8 md:h-10 w-auto object-contain opacity-70 flex-shrink-0"
                loading="lazy"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Transform + Description Section */}
      <section className="relative bg-cream-100 pt-3 md:pt-4 pb-20 md:pb-28 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <FadeUp>
            <h1 className="text-[3.5rem] md:text-[8rem] lg:text-[10rem] font-black text-foreground leading-[0.85] tracking-tighter uppercase mb-16 md:mb-40">
              EN FRISKARE<br />ARBETSPLATS
            </h1>
          </FadeUp>
          <FadeUp delay={200} className="flex flex-col items-start text-left">
            <div className="max-w-md w-full text-left">
              <p className="text-lg md:text-xl text-foreground/80 leading-relaxed mb-8">
                Vitaminer och mineraler direkt på kontoret. Enkelt, gott och smart, för en arbetsplats som mår bättre.
              </p>
              <Link
                to="/produkter"
                className="text-sm font-medium text-foreground tracking-wide hover:underline transition-all duration-200"
              >
                SE VÅRA PRODUKTER →
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Story Section — asymmetric editorial layout */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-8 md:gap-6 items-center">
          <RevealDiv className="md:col-span-3">
            <AnimatedImage
              src={dispenserVarIde}
              alt="Aquadias vitamindispenser"
              className="w-full aspect-[3/4] object-cover shadow-lg"
            />
          </RevealDiv>
          <RevealDiv fromRight delay={150} className="md:col-span-9 flex flex-col items-end">
            <div className="flex flex-col items-start text-left">
              <h2 className="text-4xl md:text-6xl lg:text-7xl font-black text-foreground leading-[0.9] tracking-tight uppercase mb-6">
                VÅR IDÉ
              </h2>
              <p className="text-base md:text-lg text-muted-foreground max-w-md leading-relaxed mb-4">
                Vi gör det enkelt att må bra. Med smarta dispensrar och brustabletter i naturliga smaker hjälper vi arbetsplatser att investera i hälsa, varje dag.
              </p>
              <Link
                to="/om-oss"
                className="text-sm font-medium text-foreground tracking-wide hover:underline transition-all duration-200"
              >
                LÄS VÅR BERÄTTELSE →
              </Link>
            </div>
          </RevealDiv>
        </div>
      </section>

      {/* Full-bleed section */}
      <section className="relative min-h-[70vh] flex flex-col justify-end overflow-hidden">
        <img
          src={handDispenser}
          alt="Hand som använder Aquadias vitamindispenser"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-foreground/30" />
        <div className="relative z-10 px-6 md:px-12 pb-10 md:pb-16 max-w-7xl w-full">
          <FadeUp>
            <p className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-[0.9] tracking-tight uppercase mb-2">
              VÅR DISPENSER
            </p>
          </FadeUp>
          <FadeUp delay={150}>
            <p className="text-lg md:text-2xl text-white/80 font-light mb-6">
              Hållbar, enkel och stilren design
            </p>
            <Link
              to="/tjanster"
              className="text-sm font-medium text-white/90 tracking-wide hover:underline transition-all duration-200"
            >
              SE VÅR TJÄNST →
            </Link>
          </FadeUp>
        </div>
      </section>

    </Layout>
  );
};

export default Index;
